import React from "react"

import { IDog, IEvent } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";

import { IDogInfo, IShowDogs } from "../../routes/EventRegister/EventRegister";
import { toast } from "../Toast/ToastManager";
import DogPortrait from "../DogPortrait/DogPortrait";

import "./index.scss";

interface IProps {
  label?: string
  club?: string,
  show?: IShowDogs
  dogs: IDog[]
  selectedDogs: IDogInfo[]
  hasAvailableSpot: boolean
  onDogClick: (dog: IDog) => void
  onAddDog: () => void
  onEditDog?: (dog: IDog) => void
  event?: IEvent
  isAdmin?: boolean
  isDisabled?: boolean
  showSelectionHint?: boolean
}

const DogList: React.FC<IProps> = ({
                                     label = undefined,
                                     club = undefined,
                                     show = undefined,
                                     dogs,
                                     selectedDogs,
                                     hasAvailableSpot,
                                     onDogClick,
                                     onAddDog,
                                     onEditDog = undefined,
                                     event = undefined,
                                     isAdmin = undefined,
                                     isDisabled = false,
                                     showSelectionHint = false
                                   }: IProps): JSX.Element => {

  const handleDogClick = (e: React.MouseEvent<HTMLLIElement>, dog: IDog) => {
    // Prevent default behavior and stop propagation to avoid page scrolling
    e.preventDefault();
    e.stopPropagation();
    
    // Store current scroll position
    const scrollY = window.scrollY || window.pageYOffset;
    
    if (hasAvailableSpot || !show?.max_dogs) {
      onDogClick(dog);
      
      // Restore scroll position after state update
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollY);
      });
    } else {
      toast.show({
        title: "Dog Registration",
        content: "There aren't any available spots for this show",
        duration: 10000,
        type: "fail"
      });
    }
  }

  const getDogCallNameByClub = (dog?: IDogInfo) => {
    const clubRegistration = dog?.club_registrations?.find(cr => cr.sanctioning_club === club);

    return clubRegistration?.call_name ?? dog?.call_name;
  }

  // Helper function to detect dogs with only legacy registration data
  const hasLegacyRegistrationOnly = (dog: IDog, sanctioningClub: string) => {
    // Check if they have new club registration for this sanctioning club
    const hasNewRegistration = dog.club_registrations?.some(cr => {
      const matches = cr.sanctioning_club === sanctioningClub;
      const hasNumber = !!cr.registration_number;
      const hasType = !!cr.registration_type;
      
      return matches && hasNumber && hasType;
    });
    
    if (hasNewRegistration) return false;
    
    // Check if they have legacy registration data
    const hasLegacyData = dog.registration_numbers?.some(rn => {
      if (sanctioningClub === Constants.sanctioning_club.PWDCA) {
        return rn.name === Constants.sanctioning_club.CKC || rn.name === Constants.sanctioning_club.AKC;
      }
      return rn.name === sanctioningClub;
    });
    
    return hasLegacyData;
  };



  return (
    <div className="dogsList">
      {!!label && <p className="listLabel">{label}</p>}
      {showSelectionHint && <p className="selectionHint">Click or tap a dog's photo to add them to this trial — click again to remove them.</p>}
      <ul className="dogs">
        {dogs.length === 0 && (
          <li className="emptyState">
            <span>You don't have any dogs in your profile yet.</span>
          </li>
        )}
        {dogs.map((dog: IDogInfo, index: number) => {
          // Check if this dog needs migration for the current event
          const needsMigration = event && hasLegacyRegistrationOnly(dog, event.sanctioning_club);
          
          return (
            <li
              key={index}
              className={`${selectedDogs.map((sd) => sd._id).includes(dog._id) ? "dog selected" : "dog"} ${needsMigration ? "needsUpdate" : ""}`}
              onClick={(e) => { handleDogClick(e, dog); }}
            >
              <DogPortrait
                className={`${selectedDogs.map((sd) => sd._id).includes(dog._id) ? "selected" : "notSelected"} ${needsMigration ? "needsUpdate" : ""}`}
                edit
                showName
                show={show}
                dogCallName={getDogCallNameByClub(dog)}
                dog={dog}
                onEditDog={onEditDog}
                needsRegistrationUpdate={needsMigration}
                sanctioningClub={event?.sanctioning_club}
                isAdmin={isAdmin}
                isDisabled={isDisabled}
              />
            </li>
          );
        })}
        {typeof onAddDog === "function" && <li className="addDog" onClick={onAddDog}>
          <div className="iconWrap">
            <div className="icon plus"/>
          </div>
          <span>New Dog</span>
        </li>}
      </ul>
    </div>
  )
}

export default DogList
